import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useActiveObject } from '@/components/app/editor/active-object-context'
import {
  TRANSPARENCY_MAX,
  TRANSPARENCY_MIN,
  TRANSPARENCY_PRESETS,
  TRANSPARENCY_RANGE_ERROR,
  clampTransparencyPercent,
  isTransparencyPercentInRange,
  matchingTransparencyPreset,
  parseTransparencyPercent,
} from '@/lib/object-effects/transparency'
import {
  type TransparencySelectionItem,
  committedTransparencyFromManagers,
  transparencySelectionItems,
} from '../helpers/transparency-selection'
import type { TransparencyControlProps } from '../types/effect-control-props'
import type { EffectSliderBindings } from '../types/effect-slider-bindings'
import { useTransparencyDocument } from './use-transparency-document'

/**
 * Transparency field state machine. Unlike the geometry effects it follows the `activeObjects`
 * mirror (group shells expand to their descendants), shows the first target's value for a
 * multi-selection, commits a blur synchronously, and reports each committed change through
 * `onTransparencyCommitApplied`.
 */
export function useTransparencyControl({
  disabled = false,
  shapeManagerRef,
  onShapeCommitted,
  onBatchShapesCommitted,
  onTransparencyCommitApplied,
}: TransparencyControlProps): EffectSliderBindings {
  const { activeObjects } = useActiveObject()
  const [sliderValue, setSliderValue] = useState([TRANSPARENCY_MIN])
  const [inputText, setInputText] = useState(String(TRANSPARENCY_MIN))
  const [error, setError] = useState<string | undefined>(undefined)
  const [selectPreset, setSelectPreset] = useState<string | null>(null)
  const { applyTransparencyLive, commitWithHistory, beginSession, clearSession } =
    useTransparencyDocument({
      activeObjects,
      shapeManagerRef,
      onShapeCommitted,
      onBatchShapesCommitted,
    })

  const items = useMemo<TransparencySelectionItem[]>(() => {
    const sm = shapeManagerRef?.current

    return transparencySelectionItems(activeObjects, sm?.getShapes() ?? activeObjects)
  }, [activeObjects, shapeManagerRef])
  const hasSelection = items.length > 0
  const controlDisabled = disabled || !hasSelection
  const syncToken = useMemo(() => items.map((i) => `${i.id}:${i.value}`).join('|'), [items])

  /* eslint-disable react-hooks/exhaustive-deps -- only syncToken in deps */
  useEffect(() => {
    if (items.length === 0) {
      setSliderValue([TRANSPARENCY_MIN])
      setInputText(String(TRANSPARENCY_MIN))
      setSelectPreset(matchingTransparencyPreset(TRANSPARENCY_MIN))
      return
    }

    const vals = items.map((i) => i.value)
    const first = vals[0] ?? TRANSPARENCY_MIN
    const allSame = vals.every((v) => v === first)
    const n = allSame ? first : (vals[0] ?? TRANSPARENCY_MIN)

    setSliderValue([n])
    setInputText(String(n))
    setError(undefined)
    setSelectPreset(matchingTransparencyPreset(n))
  }, [syncToken])
  /* eslint-enable react-hooks/exhaustive-deps */

  const syncControlsToCommitted = useCallback((committed: number) => {
    setSliderValue([committed])
    setSelectPreset(matchingTransparencyPreset(committed))
  }, [])

  const handleSliderChange = (value: number[]) => {
    setError(undefined)
    const n = value[0] ?? TRANSPARENCY_MIN

    setSliderValue([n])
    setInputText(String(n))
    setSelectPreset(matchingTransparencyPreset(n))
    applyTransparencyLive(n)
  }

  const handleSliderCommit = (value: number[]) => {
    const n = value[0] ?? TRANSPARENCY_MIN

    const committed = commitWithHistory(n)

    clearSession()
    if (committed) {
      onTransparencyCommitApplied?.()
    }
  }

  const tryCommitInput = useCallback(
    (options?: { revertInvalid?: boolean }): boolean => {
      const revertInvalid = options?.revertInvalid === true
      const raw = inputText.trim()
      const committed = committedTransparencyFromManagers(activeObjects, shapeManagerRef)

      if (raw === '') {
        setInputText(String(committed))
        syncControlsToCommitted(committed)
        setError(undefined)
        return true
      }

      const parsed = parseTransparencyPercent(raw)

      if (!parsed.ok || !isTransparencyPercentInRange(Math.round(parsed.value))) {
        // Enter/Tab-away validation: keep invalid text and show inline error.
        // Canvas blur / focus leaving the field: revert to last committed value (Scenario 3).
        if (revertInvalid) {
          setInputText(String(committed))
          syncControlsToCommitted(committed)
          setError(undefined)
        } else {
          setError(TRANSPARENCY_RANGE_ERROR)
          syncControlsToCommitted(committed)
        }

        return false
      }

      const v = clampTransparencyPercent(parsed.value)

      setError(undefined)
      beginSession()
      setSliderValue([v])
      setInputText(String(v))
      setSelectPreset(matchingTransparencyPreset(v))
      applyTransparencyLive(v)
      const didCommitHistory = commitWithHistory(v)

      clearSession()
      if (didCommitHistory) {
        onTransparencyCommitApplied?.()
      }

      return true
    },
    [
      activeObjects,
      applyTransparencyLive,
      beginSession,
      clearSession,
      commitWithHistory,
      inputText,
      onTransparencyCommitApplied,
      shapeManagerRef,
      syncControlsToCommitted,
    ]
  )

  const handlePresetChange = (val: string) => {
    if (controlDisabled) return
    const option = TRANSPARENCY_PRESETS.find((opt) => opt.label === val)

    if (option) {
      beginSession()
      setSliderValue([option.value])
      setInputText(String(option.value))
      setSelectPreset(val)
      applyTransparencyLive(option.value)
      const committed = commitWithHistory(option.value)

      clearSession()
      if (committed) {
        onTransparencyCommitApplied?.()
      }
    }
  }

  return {
    presets: TRANSPARENCY_PRESETS,
    selectedPreset: selectPreset,
    onPresetChange: handlePresetChange,
    sliderValue,
    min: TRANSPARENCY_MIN,
    max: TRANSPARENCY_MAX,
    onSliderChange: handleSliderChange,
    onSliderCommit: handleSliderCommit,
    onSessionStart: beginSession,
    inputText,
    inputHandlers: {
      onFocus: beginSession,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        setError(undefined)
        setInputText(e.target.value)
      },
      onBlur: () => {
        // Blur = focus leaving the field (canvas click, Tab, tool change).
        // Invalid values must revert to the last committed transparency (Scenario 3).
        void tryCommitInput({ revertInvalid: true })
      },
      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          // Enter keeps focus on invalid input so the inline error is visible.
          if (tryCommitInput({ revertInvalid: false })) {
            ;(e.target as HTMLInputElement).blur()
          }
        }
      },
    },
    error,
    disabled: controlDisabled,
    dimmed: controlDisabled,
  }
}
